// src/pages/Login.js
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import axios from '../utils/axiosInstance';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/'; // بازگشت به صفحه قبلی (مثل سبد خرید)

  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' یا 'otp'
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [errors, setErrors] = useState({
    phone: '',
    password: '',
    otp: '',
    general: '',
  });

  // اعتبارسنجی شماره موبایل
  const validatePhone = () => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length !== 11 || !cleaned.startsWith('09')) {
      setErrors(prev => ({ ...prev, phone: 'شماره موبایل معتبر نیست (مثال: ۰۹۱۲۳۴۵۶۷۸۹)' }));
      return false;
    }
    setErrors(prev => ({ ...prev, phone: '' }));
    return true;
  };

  // ارسال کد تأیید
  const handleSendOtp = async () => {
    if (!validatePhone()) return;

    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
      await axios.post('/auth/send-otp', { phone_number: phoneNumber });
      setOtpSent(true);
      setErrors(prev => ({ ...prev, general: 'کد تأیید با موفقیت ارسال شد' }));
    } catch (error) {
      const message = error.response?.data?.message || 'خطا در ارسال کد تأیید. دوباره تلاش کنید.';
      setErrors(prev => ({ ...prev, general: message }));
    } finally {
      setIsLoading(false);
    }
  };

  // ورود نهایی
  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({ phone: '', password: '', otp: '', general: '' });

    if (!validatePhone()) return;

    setIsLoading(true);

    try {
      let response;

      if (loginMethod === 'password') {
        if (password.length < 6) {
          setErrors(prev => ({ ...prev, password: 'رمز عبور باید حداقل ۶ کاراکتر باشد' }));
          return;
        }

        response = await axios.post('/auth/login/password', {
          phone_number: phoneNumber,
          password,
        });
      } else {
        if (!otpSent) {
          setErrors(prev => ({ ...prev, otp: 'ابتدا کد تأیید را دریافت کنید' }));
          return;
        }

        if (otp.length !== 6) {
          setErrors(prev => ({ ...prev, otp: 'کد تأیید باید دقیقاً ۶ رقم باشد' }));
          return;
        }

        response = await axios.post('/auth/login/otp', {
          phone_number: phoneNumber,
          otp,
        });
      }

      const { accessToken, user } = response.data;

      // ذخیره در Zustand
      useAuthStore.getState().login(user, accessToken);
      localStorage.setItem('token', accessToken);
      
      // پیام خوش‌آمدگویی
      alert(`خوش آمدید ${user.name || user.phone_number} عزیز! 👋`);

      // هدایت به صفحه قبلی (مثل سبد خرید) یا خانه
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.message || 'خطایی در ورود رخ داد. دوباره تلاش کنید.';
      if (loginMethod === 'password') {
        setErrors(prev => ({ ...prev, password: message }));
      } else {
        setErrors(prev => ({ ...prev, otp: message }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gold-light/20 via-white to-gold-light/20 py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* هدر طلایی */}
          <div className="bg-gradient-to-r from-gold to-gold-dark text-white py-10 px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold">ورود به خرمی‌گلد</h1>
            <p className="mt-3 text-gold-light text-lg">با شماره موبایل وارد شوید</p>
          </div>

          <div className="p-8 md:p-12">
            {/* پیام عمومی */}
            {errors.general && (
              <div className={`text-center mb-6 p-4 rounded-xl font-medium ${errors.general.includes('موفقیت') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {errors.general}
              </div>
            )}

            {/* شماره موبایل */}
            <div className="mb-8">
              <label className="block text-gray-700 font-semibold mb-2 text-lg">شماره موبایل</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
                maxLength="11"
                className={`w-full px-6 py-4 text-lg border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-gold/30 transition ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                dir="ltr"
                disabled={isLoading}
              />
              {errors.phone && <p className="text-red-600 text-sm mt-2 text-right">{errors.phone}</p>}
            </div>

            {/* تب‌های روش ورود */}
            <div className="flex mb-8 border-b-2 border-gray-200">
              <button
                type="button"
                onClick={() => setLoginMethod('password')}
                disabled={isLoading}
                className={`flex-1 py-4 text-xl font-bold transition ${
                  loginMethod === 'password'
                    ? 'text-gold-dark border-b-4 border-gold-dark'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                رمز عبور
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('otp')}
                disabled={isLoading}
                className={`flex-1 py-4 text-xl font-bold transition ${
                  loginMethod === 'otp'
                    ? 'text-gold-dark border-b-4 border-gold-dark'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                کد پیامکی
              </button>
            </div>

            <form onSubmit={handleLogin} className="space-y-8">
              {/* ورود با رمز عبور */}
              {loginMethod === 'password' && (
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-lg">رمز عبور</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-6 py-4 text-lg border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-gold/30 transition ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="رمز عبور خود را وارد کنید"
                    disabled={isLoading}
                  />
                  {errors.password && <p className="text-red-600 text-sm mt-2 text-right">{errors.password}</p>}
                </div>
              )}

              {/* ورود با OTP */}
              {loginMethod === 'otp' && (
                <div>
                  {!otpSent ? (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isLoading || phoneNumber.length !== 11}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-5 rounded-2xl text-xl transition shadow-lg"
                    >
                      {isLoading ? 'در حال ارسال کد...' : 'دریافت کد تأیید'}
                    </button>
                  ) : (
                    <>
                      <label className="block text-gray-700 font-semibold mb-2 text-lg text-center">
                        کد ۶ رقمی دریافت‌شده را وارد کنید
                      </label>
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength="6"
                        autoComplete="one-time-code"
                        className={`w-full px-6 py-5 text-3xl text-center tracking-widest font-mono border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-gold/30 transition ${
                          errors.otp ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="------"
                        dir="ltr"
                        autoFocus
                        disabled={isLoading}
                      />
                      {errors.otp && <p className="text-red-600 text-sm mt-2 text-center">{errors.otp}</p>}

                      <p className="text-center mt-6 text-gray-600">
                        کد را دریافت نکردید؟{' '}
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={isLoading}
                          className="text-gold-dark font-bold hover:underline"
                        >
                          ارسال مجدد
                        </button>
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* دکمه ورود نهایی */}
              <button
                type="submit"
                disabled={
                  isLoading ||
                  !phoneNumber ||
                  (loginMethod === 'password' && !password) ||
                  (loginMethod === 'otp' && !otpSent)
                }
                className="w-full bg-gold hover:bg-gold-dark disabled:bg-gray-400 text-white font-bold py-6 rounded-2xl text-2xl transition transform hover:scale-105 shadow-2xl mt-8"
              >
                {isLoading ? 'در حال ورود...' : 'ورود به حساب'}
              </button>
            </form>

            <p className="text-center mt-10 text-gray-600 text-lg">
              حساب کاربری ندارید؟{' '}
              <Link to="/register" className="text-gold-dark font-bold hover:underline text-xl">
                همین حالا ثبت‌نام کنید
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;